import React, { ReactNode, useRef, useState } from 'react'
import styled, { css } from 'styled-components'

import { Icon, Popup, Box } from 'components'
import Text from 'components/Text'

import { ButtonVariant } from 'components/Buttons/Button'
import { ButtonComponentType } from 'components/Footer'
import ConfirmationModal from 'components/ConfirmationModal'
import useClickOutSide from 'hooks/use-click-outside-hooks'

import { colors } from 'utils/colors'
import { Z_INDEX } from 'utils/constants'

/**
 * Right Panel is a simplified reusable implementation of Popup
 * With fullheight, fixed width and right position
 */

type RightPanelProps = {
  cancelLabel?: string
  children?: React.ReactNode
  disableSubmit?: boolean
  isSubmitting?: boolean
  onCancel?: () => void
  onSubmit?: () => void
  padded?: boolean
  leftButtonComponent?: ButtonComponentType
  rightButtonComponent?: ButtonComponentType
  submitIcon?: string | string[]
  submitIconColor?: keyof typeof colors
  submitLabel?: string
  submitLabelColor?: keyof typeof colors
  submitVariant?: ButtonVariant
  title: string | React.ReactNode
  titleIcon?: string | string[]
  width?: number
  withPrimaryButton?: boolean
  isStickyFooter?: boolean
  isShowFooter?: boolean
  withCancelConfirmation?: boolean
  withSubmitConfirmation?: boolean
  addonFooter?: React.ReactNode
  closeOnClickOutside?: boolean
  zIndex?: number
  hideBackdrop?: boolean
  contentPosition?: 'relative' | 'static' | 'initial'
  disableSecondaryOnSubmitting?: boolean
}

const RightPanel = ({
  cancelLabel = 'cancel_btn',
  children,
  disableSubmit = false,
  isSubmitting = false,
  onSubmit = () => {},
  onCancel = () => {},
  padded = true,
  leftButtonComponent,
  rightButtonComponent,
  submitIconColor,
  submitIcon,
  submitLabel = 'save_btn',
  submitLabelColor,
  submitVariant = 'fill',
  title,
  titleIcon,
  width = 500,
  withPrimaryButton = true,
  isStickyFooter = true,
  isShowFooter = true,
  withCancelConfirmation = false,
  withSubmitConfirmation = false,
  addonFooter,
  closeOnClickOutside = false,
  zIndex,
  hideBackdrop = false,
  contentPosition = 'relative',
  disableSecondaryOnSubmitting = true,
}: RightPanelProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  useClickOutSide(containerRef, () => {
    closeOnClickOutside && onCancel()
  })
  const [modal, setModal] = useState<ReactNode>(null)
  return (
    <>
      <Popup
        zIndex={zIndex ?? Z_INDEX.panel}
        containerRef={containerRef}
        fullHeight
        isSubmitting={isSubmitting}
        disableSecondaryOnSubmitting={disableSecondaryOnSubmitting}
        labelPrimary={withPrimaryButton ? submitLabel : undefined}
        labelSecondary={cancelLabel}
        onClickPrimary={() => {
          if (disableSubmit) return

          if (withSubmitConfirmation) {
            setModal(
              <ConfirmationModal
                variant="save"
                cancel={() => setModal(null)}
                submit={() => {
                  onSubmit()
                  setModal(null)
                }}
              />,
            )
            return
          }

          onSubmit()
        }}
        onClickSecondary={() => {
          if (withCancelConfirmation) {
            setModal(
              <ConfirmationModal
                variant="discard"
                cancel={() => setModal(null)}
                submit={() => {
                  onCancel()
                  setModal(null)
                }}
              />,
            )
            return
          }

          onCancel()
        }}
        position="right"
        primaryButtonIcon={submitIcon}
        primaryButtonIconColor={submitIconColor}
        primaryButtonVariant={submitVariant}
        labelPrimaryColor={submitLabelColor}
        leftButtonComponent={leftButtonComponent}
        rightButtonComponent={rightButtonComponent}
        width={`${width}px`}
        isStickyFooter={isStickyFooter}
        isShowFooter={isShowFooter}
        isDisabledPrimary={disableSubmit}
        addonFooter={addonFooter}
        hideBackdrop={hideBackdrop}
      >
        <TitleWrapper>
          {titleIcon && <TitleIcon icon={titleIcon} />}
          {typeof title === 'string' ? (
            <Text inline width="100%">
              {title}
            </Text>
          ) : (
            title
          )}
        </TitleWrapper>
        <Box overflow="auto" height="calc(100vh - 50px - 70px)" position={contentPosition}>
          <Content padded={padded}>{children}</Content>
        </Box>
      </Popup>
      {modal}
    </>
  )
}

const TitleWrapper = styled.div`
  border-bottom: 1px solid ${(p) => p.theme.colors.gray4};
  padding: 1rem;
`

const TitleIcon = styled(Icon)`
  margin-right: 15px;
`

const Content = styled.div<{ padded?: boolean }>`
  box-sizing: border-box;
  height: 100%;
  flex-grow: 1;
  ${(p) =>
    p.padded &&
    css`
      padding: 18px 24px;
    `}
`

export default RightPanel
